import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useAuthStore } from '@/lib/stores/authStore';
import { CabinetHubView, type RefreshState } from './CabinetHubView';

/**
 * Container Экрана 2.11.1 «Личный кабинет» — Block 11 Phase 11.A (ADR-0005).
 *
 * Держит state + side effects; UI — чистый presenter `CabinetHubView`.
 *
 * **Источники данных (11.A — без новых контрактов):**
 *   - `authStore`: `profile` (имя/телефон/специализация, in-memory P-009),
 *     `city` (persisted), `subscription` (server source of truth), `logout`,
 *     `getDaysRemaining`.
 *   - `apiClient.me.get()` (существующий `GET /api/me`): mount-fetch подписки
 *     при прямом входе/refresh на `/cabinet`, когда in-memory `subscription`
 *     ещё `null` (mirror App.tsx Phase 4, ADR-0014 Q6). Guard `subscription
 *     === null` против refetch-loop. На ошибке — не делаем агрессивный logout
 *     (в отличие от App.tsx на `/`): просто оставляем карточку в fallback.
 *   - Сообщения — **мок** (0 непрочитанных → пустое состояние). Реальный
 *     messages-API вводится позже (контракт не добавляем сейчас).
 *
 * **«Обновить базу декоров и фурнитуры»** — рабочая кнопка с 5 async-состояниями
 * (P-021): idle → loading → success (заглушка действия). Реальный
 * Service-Worker-refresh офлайн-каталога — TODO отдельным шагом.
 */

// Мок 11.A: непрочитанных сообщений нет. Реальный счётчик — будущий messages-API.
const MOCK_UNREAD_MESSAGES = 0;

// Длительность заглушки «обновления базы» до перехода loading → success.
const REFRESH_STUB_DURATION_MS = 800;

export function CabinetHub() {
  const navigate = useNavigate();

  const profile = useAuthStore((s) => s.profile);
  const city = useAuthStore((s) => s.city);
  const jwt = useAuthStore((s) => s.jwt);
  const subscription = useAuthStore((s) => s.subscription);
  const setSubscription = useAuthStore((s) => s.setSubscription);
  const logout = useAuthStore((s) => s.logout);
  const getDaysRemaining = useAuthStore((s) => s.getDaysRemaining);

  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [refreshState, setRefreshState] = useState<RefreshState>('idle');
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mount-fetch подписки при прямом входе/refresh на /cabinet (subscription
  // не persist'ится — P-009). Guard `subscription === null` против loop.
  useEffect(() => {
    if (jwt === null || subscription !== null) return;
    let cancelled = false;
    setSubscriptionLoading(true);
    apiClient.me
      .get()
      .then((me) => {
        if (cancelled) return;
        setSubscription({
          status: me.subscriptionStatus,
          trialStartedAt: me.trialStartedAt,
          expiresAt: me.expiresAt,
        });
      })
      .catch(() => {
        // 401 / network / parse — оставляем subscription=null (fallback-карточка).
        // НЕ logout (не выкидываем пользователя из открытого ЛК).
      })
      .finally(() => {
        if (!cancelled) setSubscriptionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, subscription, setSubscription]);

  // Cleanup refresh-таймера на unmount.
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const handleRefreshDatabase = useCallback(() => {
    setRefreshState((prev) => {
      if (prev === 'loading') return prev;
      if (refreshTimerRef.current !== null) clearTimeout(refreshTimerRef.current);
      // TODO(11.A follow-up): подключить реальный Service-Worker-refresh офлайн-
      // каталога. Сейчас — заглушка действия с корректным переходом состояний.
      refreshTimerRef.current = setTimeout(() => {
        setRefreshState('success');
        refreshTimerRef.current = null;
      }, REFRESH_STUB_DURATION_MS);
      return 'loading';
    });
  }, []);

  const handleLogout = useCallback(() => {
    // БЕЗ подтверждения (PRD §2.11.1 — деструктивное действие отделено
    // визуально, диалог не нужен: сессия восстанавливается по телефону).
    logout();
    navigate('/');
  }, [logout, navigate]);

  return (
    <CabinetHubView
      profile={profile}
      city={city}
      subscription={subscription}
      subscriptionLoading={subscriptionLoading}
      daysRemaining={getDaysRemaining()}
      unreadMessagesCount={MOCK_UNREAD_MESSAGES}
      refreshState={refreshState}
      onEditProfile={() => navigate('/cabinet/profile')}
      onSubscriptionAction={() => navigate('/cabinet/subscription')}
      onRenewEarly={() => navigate('/cabinet/subscription?promo=AUTORENEW_3D')}
      onReferral={() => navigate('/cabinet/referral')}
      onReadMessages={() => navigate('/cabinet/messages')}
      onRefreshDatabase={handleRefreshDatabase}
      onLogout={handleLogout}
    />
  );
}
